import { useCallback, useContext, useEffect, useState } from "react";

import { Close, Upload } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from "@mui/material";
import Axios from "axios";

import { UsersApiHelperForMember } from "../../api/ApiHelper";
import { getDocumentStructuredData } from "../../api-toolkit/FilesHelper";
import { AppContext } from "../../App";

/**
 * UsersApiHelperForMember.callPost runs @datanac/datanac-api-toolkit normalizeResponse
 * on array bodies (lowercases every key). Files/Search returns an array, so document
 * fields must be accessed via fallbacks to tolerate both casings.
 */
const docId = (doc) => doc?.documentId ?? doc?.documentid ?? doc?.DocumentId;
const docType = (doc) => doc?.documentType ?? doc?.documenttype ?? doc?.DocumentType;
const docName = (doc) => doc?.name ?? doc?.Name;
const docKey = (doc) => doc?.id ?? doc?.Id ?? docId(doc);

export const DocumentSelectDialog = ({ open, onClose, document_type_name }) => {
  const { globalState } = useContext(AppContext);
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingContents, setIsLoadingContents] = useState(false);
  const [documentPreviewUrl, setDocumentPreviewUrl] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewDocumentName, setPreviewDocumentName] = useState("");
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentSummary, setDocumentSummary] = useState("");

  // Fetch documents from the UsersAPI Files/Search relay (which authorizes the
  // agent->producer UserGrant before forwarding to the S3-backed search logic).
  const loadDocuments = async () => {
    setIsLoading(true);
    try {
      const body = {
        tenantID: import.meta.env.VITE_RGCHAT_TENANTID_TILLEY,
        userID: globalState?.producer_token || globalState?.agent_token,
        sessionID: "00000000-0000-0000-0000-000000000000",
        accessKey: "00000000-0000-0000-0000-000000000000",
        documentId: null,
        documentType: document_type_name,
        // producer_token + agent_token drive the UsersAPI relay's
        // agent->producer UserGrant authorization.
        producer_token: globalState?.producer_token,
        agent_token: globalState?.agent_token,
      };

      // callPost runs normalizeResponse on array bodies (lowercases keys), so
      // each doc has `documenttype`/`documentid`/`originalfilename` — use
      // fallback accessors below to support both casings.
      const data = await UsersApiHelperForMember.callPost("Files/Search", body, globalState);

      let filteredDocs = Array.isArray(data) ? data : [];
      if (document_type_name) {
        filteredDocs = filteredDocs.filter((doc) => docType(doc) === document_type_name);
      }

      setDocuments(filteredDocs);
    } catch (error) {
      console.error("Error loading documents:", error);
      setDocuments([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadDocuments();
    }
  }, [open, document_type_name]);

  // Cleanup blob URL on unmount or when URL changes
  useEffect(() => {
    return () => {
      if (documentPreviewUrl && documentPreviewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(documentPreviewUrl);
      }
    };
  }, [documentPreviewUrl]);

  const handleDocumentSelect = useCallback(
    async (doc) => {
      // Clean up previous preview URL if exists
      if (documentPreviewUrl && documentPreviewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(documentPreviewUrl);
      }

      setSelectedDocument(doc);
      setIsLoadingPreview(true);
      setPreviewDocumentName(docName(doc) || docType(doc) || "Document");

      try {
        const structuredData = await getDocumentStructuredData(docId(doc), false, globalState);
        const fileUrls = structuredData?.metadata;

        // Fetch the actual metadata from the metadataUrl
        if (fileUrls?.metadataUrl) {
          const metadataResponse = await Axios.get(fileUrls.metadataUrl);
          const metadata = metadataResponse.data;

          // Extract summary from metadata
          if (metadata?.summary) {
            setDocumentSummary(metadata.summary);
          } else {
            setDocumentSummary("");
          }
        } else {
          setDocumentSummary("");
        }

        // Fetch PDF for preview
        if (fileUrls?.documentUrl) {
          // Fetch PDF as blob to avoid Content-Disposition: attachment forcing download
          const response = await Axios.get(fileUrls.documentUrl, {
            responseType: "blob",
          });

          // Create blob with explicit PDF type
          const pdfBlob = new Blob([response.data], { type: "application/pdf" });
          const blobUrl = URL.createObjectURL(pdfBlob);

          setDocumentPreviewUrl(blobUrl);
        } else {
          setDocumentPreviewUrl(null);
        }
      } catch (error) {
        console.error("Error loading document preview:", error);
        setDocumentPreviewUrl(null);
        setDocumentSummary("");
      } finally {
        setIsLoadingPreview(false);
      }
    },
    [documentPreviewUrl, globalState]
  );

  const handleImportClick = async () => {
    if (!selectedDocument) return;

    setIsLoadingContents(true);
    try {
      const structuredData = await getDocumentStructuredData(docId(selectedDocument), false, globalState);
      const contents = structuredData?.content || null;
      onClose(contents);
    } catch (error) {
      console.error("Error loading document contents:", error);
      onClose(null);
    } finally {
      setIsLoadingContents(false);
    }
  };

  const handleClose = () => {
    onClose(null);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        Select Document
        {document_type_name && (
          <Typography variant="body2" color="text.secondary">
            Type: {document_type_name}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ height: "70vh" }}>
        <Grid container spacing={2} sx={{ height: "100%" }}>
          {/* Left Pane - Document List (1/3 width) */}
          <Grid item xs={4} sx={{ height: "100%", overflow: "auto" }}>
            {isLoading || isLoadingContents ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : documents.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                No documents found
                {document_type_name && ` for type: ${document_type_name}`}
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {documents.map((doc) => (
                  <Button
                    key={docKey(doc)}
                    onClick={() => handleDocumentSelect(doc)}
                    variant="outlined"
                    sx={{
                      justifyContent: "flex-start",
                      textAlign: "left",
                      py: 1,
                      px: 1.5,
                      borderWidth: 2,
                      textTransform: "none",
                      bgcolor: docKey(selectedDocument) === docKey(doc) ? "action.selected" : "transparent",
                      "&:hover": {
                        borderWidth: 2,
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <Box sx={{ width: "100%" }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {docName(doc) || docType(doc) || "---"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Type: {docType(doc)}
                      </Typography>
                    </Box>
                  </Button>
                ))}
              </Box>
            )}
          </Grid>

          {/* Right Pane - Preview (2/3 width) */}
          <Grid item xs={8} sx={{ height: "100%" }}>
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                bgcolor: "grey.100",
                borderRadius: 1,
              }}
            >
              {previewDocumentName && (
                <Box sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider", bgcolor: "white" }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {previewDocumentName}
                  </Typography>
                </Box>
              )}
              {documentSummary && (
                <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Summary
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {documentSummary}
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {isLoadingPreview ? (
                  <CircularProgress />
                ) : documentPreviewUrl ? (
                  <iframe
                    src={`${documentPreviewUrl}#view=FitH`}
                    type="application/pdf"
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                    }}
                    title="Document Preview"
                  />
                ) : (
                  <Typography color="text.secondary" variant="body2">
                    Click the preview icon to view a document
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} startIcon={<Close />} disabled={isLoadingContents}>
          Cancel
        </Button>
        <Button
          onClick={handleImportClick}
          startIcon={<Upload />}
          variant="contained"
          color="primary"
          disabled={!selectedDocument || isLoadingContents}
        >
          {isLoadingContents ? "Importing..." : "Import"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
